import { useEffect, useState, useRef } from "react";
import { api, fmtDateTime, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { DataStatePanel } from "@/components/ui/data-state";
import { ScanLine, Upload, Loader2, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function InvoiceOCR() {
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const r = await api.get("/ai/ocr-history");
      setHistory(r.data);
    } catch (e) {
      setHistoryError(parseApiError(e, "Không tải được lịch sử OCR").message);
    } finally {
      setHistoryLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setLoading(true); setActive(null);
    try {
      const r = await api.post("/ai/invoice-ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setActive(r.data);
      toast.success("Đã trích xuất hóa đơn"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR lỗi (Minimax key)");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-ocr">
      <PageHeader
        kicker="AI • Công cụ"
        title="OCR Chứng từ"
        breadcrumb={["Công cụ", "OCR Chứng từ"]}
        actions={
          <>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
            <Button onClick={() => fileRef.current?.click()} disabled={loading} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-upload-ocr">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {loading ? "Đang xử lý..." : "Upload ảnh hóa đơn"}
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          {active ? (
            <div className="bg-white border border-stone-200 rounded-sm">
              <div className="px-5 py-3.5 border-b border-stone-200 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Kết quả OCR</div>
                  <div className="font-serif text-lg font-bold">Dữ liệu trích xuất</div>
                </div>
              </div>
              <div className="p-5 space-y-3">
                {active.data && Object.keys(active.data).length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        ["invoice_number","Số hóa đơn"],
                        ["invoice_date","Ngày"],
                        ["supplier_name","Nhà cung cấp"],
                        ["supplier_tax","MST"],
                        ["buyer_name","Người mua"],
                        ["total_amount","Tổng tiền"],
                        ["vat","VAT"],
                        ["currency","Tiền tệ"],
                      ].map(([k, l]) => (
                        <div key={k} className="border-b border-stone-100 pb-2">
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{l}</div>
                          <div className="font-semibold mt-0.5">{active.data[k] ?? "—"}</div>
                        </div>
                      ))}
                    </div>
                    {Array.isArray(active.data.line_items) && active.data.line_items.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Chi tiết hàng hóa</div>
                        <table className="w-full text-sm border border-stone-200">
                          <thead className="bg-stone-50 text-xs">
                            <tr>
                              <th className="text-left px-2 py-1.5">Mô tả</th>
                              <th className="text-right px-2 py-1.5">SL</th>
                              <th className="text-right px-2 py-1.5">Đơn giá</th>
                              <th className="text-right px-2 py-1.5">Thành tiền</th>
                            </tr>
                          </thead>
                          <tbody>
                            {active.data.line_items.map((it, i) => (
                              <tr key={i} className="border-t border-stone-100">
                                <td className="px-2 py-1.5">{it.description || "—"}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{it.quantity ?? "—"}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{it.unit_price ?? "—"}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{it.amount ?? "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-stone-400 text-sm">Model trả về dữ liệu không phải JSON. Kiểm tra raw response bên dưới.</div>
                )}
                <details className="text-xs">
                  <summary className="cursor-pointer text-stone-500 hover:text-stone-700">Raw response</summary>
                  <pre className="mt-2 bg-stone-100 p-2 rounded-sm font-mono overflow-auto max-h-32">{active.raw || ""}</pre>
                </details>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-sm p-10 text-center" data-testid="ocr-empty">
              <div className="w-16 h-16 mx-auto rounded-sm bg-emerald-900 flex items-center justify-center mb-4">
                <ScanLine className="w-8 h-8 text-amber-300" />
              </div>
              <h3 className="font-serif text-2xl font-bold mb-2">OCR chứng từ với Minimax</h3>
              <p className="text-sm text-stone-600 mb-6 max-w-md mx-auto">
                Upload ảnh hóa đơn/chứng từ → AI sẽ tự động trích xuất số hóa đơn, MST, NCC, tổng tiền, VAT, chi tiết hàng hóa.
              </p>
              <Button onClick={() => fileRef.current?.click()} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm">
                <Upload className="w-4 h-4 mr-1.5" />Tải ảnh lên
              </Button>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white border border-stone-200 rounded-sm">
            <div className="px-4 py-3 border-b border-stone-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Lịch sử</div>
              <div className="font-serif text-lg font-bold">Đã trích xuất ({history.length})</div>
            </div>
            {historyLoading || historyError ? (
              <DataStatePanel
                type={historyLoading ? "loading" : "error"}
                message={historyLoading ? "Đang tải lịch sử OCR..." : historyError}
                onRetry={load}
                className="border-0 rounded-none"
              />
            ) : (
            <ul className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
              {history.map((h) => (
                <li key={h.id} className="px-4 py-3 hover:bg-stone-50">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-stone-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{h.file_name}</div>
                      <div className="text-[10px] text-stone-500 font-mono">{fmtDateTime(h.created_at)}</div>
                      {h.extracted?.invoice_number && (
                        <div className="text-xs text-emerald-800 mt-1">№ {h.extracted.invoice_number}</div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
              {history.length === 0 && (
                <li>
                  <DataStatePanel type="empty" message="Chưa có lịch sử" className="border-0 rounded-none py-6" />
                </li>
              )}
            </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
